import { Checkbox } from "@/components/ui/baseUi/checkbox"
import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { UserCell } from "@/components/ui/layout/table/userCell"
import { Separator } from "@/components/ui/baseUi/separator"
import { Text } from "@/components/ui/baseUi/text"
import { ParticipantBadge } from "@/modules/boxSlot/components/participantBadge"
import {
  useBoxSlotAddParticipant,
  useBoxSlotParticipantAbsence,
  useBoxSlotRemoveParticipant,
} from "@/modules/boxSlot/hooks/useBoxSlotSubscriber"
import { BoxStaffPermissions } from "@/modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@/modules/boxStaff/hooks/useBoxStaffPermissions"
import { cn } from "@/modules/lib/utils"
import type { SlotSubscriberEntity } from "@/modules/profile/subscriber/entities/slotSubscriberEntity"
import { BirthdayIcon } from "@images/svgIcons/birthdayIcon"
import { PlusIcon } from "@images/svgIcons/essentials/plusIcon"
import { MessagesIcon } from "@images/svgIcons/messagesIcon"
import { TrashIcon } from "@images/svgIcons/trashIcon"
import { word } from "@modules/core/utils/i18n"
import { useBoxNavigate } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { ParticipantStatus } from "@modules/profile/subscriber/subscriberTypes"
import { EllipsisMenu } from "@modules/ui/components/ellipsisMenu"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { useState } from "react"

type BoxSessionSubscriberProps = {
  slotId: string
  subscriber: SlotSubscriberEntity
  participantStatus: ParticipantStatus
  className?: string
} & React.ComponentProps<"div">

export function BoxSlotSubscriber({
  slotId,
  subscriber,
  participantStatus,
  className,
  ...props
}: BoxSessionSubscriberProps) {
  const boxNavigate = useBoxNavigate()
  const [isSelected, setIsSelected] = useState(subscriber.absent)
  const confirmPopup = useConfirmPopup()
  const canManageSlots = useBoxStaffPermission(BoxStaffPermissions.MANAGE_SLOTS)
  const { mutateAsync: setParticipantAbsence } = useBoxSlotParticipantAbsence()
  const { mutateAsync: addParticipantToSlot } = useBoxSlotAddParticipant()
  const { mutateAsync: removeParticipantToSlot } = useBoxSlotRemoveParticipant()

  const noShowToggle = async () => {
    setIsSelected(!isSelected)
    await setParticipantAbsence({ slotId, memberId: subscriber.memberId, absent: !isSelected })
  }

  const atendeesToggle = async () => {
    await addParticipantToSlot({ slotId, memberId: subscriber.memberId, participantStatus })
  }

  const sendMessage = () => {
    boxNavigate(Path.Chat.Root, { state: { userToChatTo: subscriber.memberId } })
  }

  const bodyDialog = (
    <div className="flex flex-col content-center items-center justify-center">
      <Text fontStyle="TL" className="mb-4 flex text-textDefault-body">
        {word("boxSessionSubscriber.bodyDialog.title")}
      </Text>
      <Text fontStyle="BM" className="flex w-88 text-center text-textDefault-caption">
        {word("boxSessionSubscriber.bodyDialog.body")}
      </Text>
    </div>
  )

  const removeFromSlot = () => {
    confirmPopup.show({
      title: "",
      message: bodyDialog,
      accept: async () => {
        await removeParticipantToSlot({ slotId, memberId: subscriber.memberId, participantStatus })
      },
      classNames: {
        dialogContent: "w-100",
      },
      footerProps: {
        cancelText: word("global.cancel"),
        confirmText: word("global.remove.yes"),
      },
    })
  }

  return (
    <div className={cn("flex w-full flex-col", className)} {...props}>
      <div className="my-2 flex w-full flex-row gap-2">
        <div className="flex flex-1">
          {participantStatus === ParticipantStatus.REGISTERED
            ? buttonRegister(() => void noShowToggle(), isSelected, !canManageSlots)
            : buttonPending(() => void atendeesToggle())}
        </div>
        <UserCell
          member={{
            avatar: subscriber.avatar,
            username: subscriber.username ?? undefined,
            firstname: subscriber.firstname,
            lastname: subscriber.lastname,
          }}
          subtitle={subscriber.username === "" ? "" : "@" + subscriber.username}
          avatarSize="size-13"
          className="min-w-0 flex-4 shrink cursor-pointer gap-2"
        />
        <div className="mt-1 flex flex-1 flex-row items-center gap-2">
          {subscriber.isTrial && <ParticipantBadge type="trial" />}
          {subscriber.isDropIn && <ParticipantBadge type="dropIn" />}
          {subscriber.isBirthday && <BirthdayIcon color="currentColor" className="text-textDefault-caption" />}
        </div>

        <div className="rounded-border flex flex-1 items-center justify-center font-bold">
          <EllipsisMenu propsContent={{ side: "bottom" }}>
            <DropdownMenuItem
              key="message"
              onClick={() => {
                sendMessage()
              }}
              disabled={!canManageSlots}
            >
              <MessagesIcon color="currentColor" className="text-textDefault-heading" /> {word("chat.send_message")}
            </DropdownMenuItem>
            <DropdownMenuItem
              key="remove"
              onClick={() => {
                removeFromSlot()
              }}
              disabled={!canManageSlots}
            >
              <TrashIcon color="currentColor" className="text-textDefault-heading" />{" "}
              {word("boxSessionSubscriber.ellipsisItems.removeList")}
            </DropdownMenuItem>
          </EllipsisMenu>
        </div>
      </div>
      <Separator />
    </div>
  )
}

function buttonPending(onClick: () => void) {
  return (
    <div className="rounded-border mt-1 flex flex-none items-center justify-center font-bold">
      <button className="mx-1 flex w-16 flex-col items-center" onClick={onClick}>
        <div className="flex w-full justify-center">
          <PlusIcon color="currentColor" width={24} height={24} className="text-textDefault-body" />
        </div>
        <div className="mt-1 text-center leading-none">
          <Text fontStyle="BS" className="text-textDefault-body">
            {word("boxSessionSubscriber.button.addToAtendees")}
          </Text>
        </div>
      </button>
    </div>
  )
}

function buttonRegister(onClick: () => void, isSelected: boolean, disabled: boolean) {
  return (
    <div className="rounded-border mt-1 flex flex-none items-center justify-center font-bold">
      <div className="mx-1 flex w-16 flex-col items-center">
        <Checkbox name="option" checked={isSelected} onCheckedChange={onClick} disabled={disabled} />

        <div className="mt-1 text-center">
          <Text fontStyle="BS" className="text-textDefault-body">
            {word("boxSessionSubscriber.checkbox.subtitle")}
          </Text>
        </div>
      </div>
    </div>
  )
}
